import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  HostBinding,
  input,
  output,
  signal,
  Signal,
  type WritableSignal,
} from '@angular/core';
import { AssistantChatData } from '../../model';
import { toSignal } from '@angular/core/rxjs-interop';
import { ChatsService } from '../../services/chats.service';
import { UntilDestroy } from '@ngneat/until-destroy';
import { orderBy } from 'lodash';

@UntilDestroy()
@Component({
  selector: 'chat-assistant-dropdown',
  templateUrl: './chat-assistant-dropdown.component.html',
  styleUrls: ['./chat-assistant-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatAssistantDropdownComponent {
  private selectedAssistantValue: WritableSignal<AssistantChatData> = signal(null);

  assistantsForChat: Signal<{ [key: string]: AssistantChatData }>;

  filterBy = input<(assistant: AssistantChatData) => boolean>(() => true);
  selectedAssistant = input<AssistantChatData>();
  placeholder = input<string>();
  filterPlaceholder = input<string>();
  tooltip = input<string>();
  dropdownView = input<'regular' | 'extension'>('regular');
  styles: any = input<any>({});
  showClear = input<boolean>();

  onSelectedAssistant = output<AssistantChatData>();
  onClearAssistant = output();

  assistantsList = computed(() => this.setAssistantList());

  @HostBinding('attr.isExtensionView')
  get isExtensionView() {
    return this.dropdownView() === 'extension';
  }

  get isRegularView() {
    return this.dropdownView() === 'regular';
  }

  constructor(private chatsService: ChatsService) {
    this.assistantsForChat = toSignal(this.chatsService.assistantsForChat$);
    effect(
      () => {
        this.selectedAssistantValue.set(this.selectedAssistant());
      },
      { allowSignalWrites: true }
    );
  }

  private setAssistantList(): AssistantChatData[] {
    const assistants = this.assistantsForChat();
    const filterBy = this.filterBy();
    const selectedAssistant = this.selectedAssistantValue();

    if ((!assistants || Object.values(assistants).length <= 1) && !selectedAssistant) {
      return [];
    }

    const filteredAssistants = assistants ? Object.values(assistants).filter(filterBy) : [];

    const sortedValues = orderBy(filteredAssistants, [(item) => item?.id === selectedAssistant?.id || item?.isDefault, 'name']);

    if (selectedAssistant) {
      const exists = sortedValues.find((as) => as.id === selectedAssistant.id);
      if (!exists) {
        sortedValues.unshift(selectedAssistant);
      }
    }

    return sortedValues;
  }

  onAssistantSelectOption(selectedAssistant: AssistantChatData): void {
    this.selectedAssistantValue.set(selectedAssistant);
    this.onSelectedAssistant.emit(selectedAssistant);
  }
}
